import { Link } from 'react-router-dom'
import { HiOutlineCheckCircle } from 'react-icons/hi2'
import LegalLayout from './legal/LegalLayout'
import { usePricingMode } from '../../contexts/PricingModeContext'
import { useAuth } from '../../contexts/AuthContext'

/**
 * Public pricing page. Price, trial and grace period come from
 * /api/system/config/ (GlobalBlockPolicy), the same values the subscription
 * flow charges, so this page can never disagree with checkout.
 *
 * URL: https://ovacs.com/pricing
 */
const INCLUDED = [
  'Clients, cases or projects, sessions and documents in one workspace',
  'Your files stored in your own Google Drive',
  'Roles and per-document access levels for your team',
  'Text and voice messages, comments and notifications',
  'Invite members and connect with other OVACS accounts',
  'Use it on the web and on Android',
]

const formatPrice = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

const PricingPage = () => {
  const { config, loading } = usePricingMode()
  const { isAuthenticated } = useAuth()

  const price = loading ? null : formatPrice(config?.annual_price_usd)
  const trialDays = config?.trial_days
  const graceDays = config?.grace_period_days

  return (
    <LegalLayout title="Pricing" description="OVACS pricing: one yearly plan per workspace, with a free trial.">
      <div className="text-center mb-10">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Pricing</h1>
        <p className="text-gray-600">One simple plan for your whole workspace.</p>
      </div>

      <div className="max-w-md mx-auto border-2 border-cyan-500 rounded-2xl p-8 shadow-sm">
        <p className="text-sm font-semibold text-cyan-700 uppercase tracking-wide">OVACS Annual</p>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-5xl font-bold text-gray-900">{price ? `USD ${price}` : '…'}</span>
        </div>
        <p className="text-gray-600 mt-1">per workspace, per year</p>

        {trialDays > 0 && (
          <p className="mt-5 text-sm text-cyan-950 bg-cyan-50 rounded-lg p-3">
            <strong>Free for {trialDays} days first.</strong> Your trial starts when you connect Google Drive.
            No payment card is needed.
          </p>
        )}

        <Link
          to={isAuthenticated ? '/profile-settings?tab=plan-storage' : '/register'}
          className="mt-6 block text-center bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
        >
          {isAuthenticated ? 'Manage your plan' : 'Start your free trial'}
        </Link>

        <ul className="mt-8 space-y-3">
          {INCLUDED.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
              <HiOutlineCheckCircle className="w-5 h-5 text-cyan-600 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <section className="max-w-2xl mx-auto mt-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">How billing works</h2>
        <ul className="list-disc ps-6 space-y-2 text-gray-700 text-[15px]">
          <li>Your subscription renews every year until you cancel. You can cancel at any time from your plan settings.</li>
          <li>
            You can get a full refund if you ask within 14 days of your first payment.
            See our <Link to="/refund-policy" className="text-cyan-700 underline">Refund Policy</Link>.
          </li>
          <li>Prices are in US dollars and do not include VAT.</li>
          <li>Payments are processed securely by Stripe. We never see your full card number.</li>
          {graceDays > 0 && (
            <li>If a payment fails, you keep full access for {graceDays} days while you update your card.</li>
          )}
        </ul>
        <p className="mt-6 text-sm text-gray-500">
          The full rules are in our <Link to="/terms" className="text-cyan-700 underline">Terms &amp; Conditions</Link>.
        </p>
      </section>
    </LegalLayout>
  )
}

export default PricingPage
